import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="turnstile"
export default class extends Controller {
  static values = { siteKey: String }
  static targets = ["button", "container", "input", "label"]

  connect() {
    // https://developers.cloudflare.com/turnstile/reference/testing/
    // Site key is read from data-turnstile-site-key-value attribute on the form element
    // Falls back to meta tag or default for backwards compatibility
    if (!this.hasSiteKeyValue || this.siteKeyValue === "") {
      const metaTag = document.querySelector('meta[name="turnstile-site-key"]')
      this.siteKeyValue = metaTag ? metaTag.content : ""
    }

    this.clearTurnstileError()

    this.onSubmit = this.handleSubmit.bind(this)
    this.element.addEventListener("submit", this.onSubmit)

    this.ensureTurnstileReady()
  }

  handleSubmit(event) {
    if (!this.hasInputTarget) return

    if (this.inputTarget.value === "") {
      event.preventDefault()
      this.startTurnstile()
    }
  }

  ensureTurnstileReady() {
    if (window.turnstile) {
      return Promise.resolve()
    }

    if (window.__turnstileReadyPromise) {
      return window.__turnstileReadyPromise
    }

    window.__turnstileReadyPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-turnstile-script="true"]')
      if (existingScript) {
        existingScript.addEventListener("load", () => {
          window.turnstile ? resolve() : reject(new Error("Turnstile loaded but unavailable"))
        })
        existingScript.addEventListener("error", () => {
          reject(new Error("Turnstile failed to load"))
        })
        return
      }

      const script = document.createElement("script")
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      script.defer = true
      script.async = true
      script.dataset.turnstileScript = "true"
      script.addEventListener("load", () => {
        window.turnstile ? resolve() : reject(new Error("Turnstile loaded but unavailable"))
      })
      script.addEventListener("error", () => {
        reject(new Error("Turnstile failed to load"))
      })
      document.body.appendChild(script)
    })

    return window.__turnstileReadyPromise
  }

  async startTurnstile() {
    this.clearTurnstileError()
    this.disableButton()

    if (!this.siteKeyValue) {
      this.showTurnstileError("Turnstile site key is missing. Please configure CLOUDFLARE_TURNSTILE_SITE_KEY.")
      this.enableButton()
      return
    }

    try {
      await this.ensureTurnstileReady()
      this.initTurnstile()
    } catch (error) {
      this.showTurnstileError("Turnstile failed to load. Please disable blockers or try again.")
      this.enableButton()
    }
  }

  disconnect() {
    if (this.onSubmit) {
      this.element.removeEventListener("submit", this.onSubmit)
    }
    if (this.widgetId) {
      if (window.turnstile) {
        turnstile.remove(this.widgetId)
      }
    }
  }

  disableButton() {
    if (!this.hasButtonTarget) return

    this.buttonTarget.disabled = true
    this.buttonTarget.setAttribute("aria-busy", "true")
    this.buttonTarget.dataset.turnstileDisabled = "true"
  }

  enableButton() {
    if (!this.hasButtonTarget) return

    this.buttonTarget.disabled = false
    this.buttonTarget.removeAttribute("aria-busy")
    delete this.buttonTarget.dataset.turnstileDisabled
  }

  initTurnstile() {
    this.disableButton()

    try {
      this.widgetId = turnstile.render(this.containerTarget, {
        sitekey: this.siteKeyValue,
        theme: "dark",
        "before-interactive-callback": () => {
          this.disableButton()
          this.containerTarget.classList.remove("hidden")
        },
        "after-interactive-callback": () => {
          this.containerTarget.classList.add("hidden")
          this.enableButton()
        },
        "error-callback": () => {
          this.showTurnstileError("Turnstile failed. Please try again.")
          this.enableButton()
        },
        "expired-callback": () => {
          if (this.hasInputTarget) {
            this.inputTarget.value = ""
          }
          this.showTurnstileError("Captcha expired. Please try again.")
          this.enableButton()
        },
        "timeout-callback": () => {
          this.showTurnstileError("Captcha timed out. Please try again.")
          this.enableButton()
        },
        callback: (token) => {
          // Success!
          if (this.hasInputTarget) {
            this.inputTarget.value = token
            if (this.inputTarget.form.requestSubmit) {
              this.inputTarget.form.requestSubmit()
            } else {
              this.inputTarget.form.submit()
            }
          }
        },
      })
    } catch (error) {
      this.showTurnstileError("Turnstile failed to initialize. Please try again.")
      this.enableButton()
    }
  }

  showTurnstileError(message) {
    if (!this.hasLabelTarget) return

    this.labelTarget.textContent = message
    this.labelTarget.classList.remove("hidden")
  }

  clearTurnstileError() {
    if (!this.hasLabelTarget) return

    this.labelTarget.textContent = ""
    this.labelTarget.classList.add("hidden")
  }
}
